import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

/**
 * Privacy-safe page tracking.
 *
 * - Only fires when a user is authenticated (we need a user_id to join signup date)
 * - Generates a session_id once per tab (sessionStorage → cleared on tab close)
 * - NO cookies, NO IP tracking, NO content/click capture
 * - Sends page_key (a known short slug), not the full URL
 * - Uses navigator.sendBeacon on unload so no requests block navigation
 *
 * Dropped in App.js so every routed page counts automatically.
 */

const PATH_TO_KEY = {
  "/": "home",
  "/app": "welcome-hub",
  "/admin": "dashboard",
  "/my-plan": "my-plan",
  "/profile": "profile",
  "/soil-analysis": "soil-analysis",
  "/spray-forecast": "spray-forecast",
  "/spreader-settings": "spreader-settings",
  "/equipment": "equipment",
  "/weed": "weed",
  "/pest": "pest",
  "/disease": "disease",
  "/pgr": "pgr",
  "/field-guide": "field-guide",
  "/calendar": "calendar",
  "/calculator": "calculator",
  "/irrigation": "irrigation",
  "/inspections": "inspections",
  "/marketplace": "marketplace",
  "/merch": "merch",
  "/partners": "partners",
  "/clients": "clients",
  "/measure": "measure",
  "/try": "try-diagnosis",
  "/pro": "pro-landing",
  "/learn": "learn",
};

const pathToPageKey = (path) => {
  if (PATH_TO_KEY[path]) return PATH_TO_KEY[path];
  // Strip dynamic segments; match on first segment
  const first = "/" + (path.split("/").filter(Boolean)[0] || "");
  return PATH_TO_KEY[first] || "other";
};

const getSessionId = () => {
  try {
    let sid = window.sessionStorage.getItem("tp_session_id");
    if (!sid) {
      sid = (crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2));
      window.sessionStorage.setItem("tp_session_id", sid);
    }
    return sid;
  } catch {
    return "no-storage-" + Date.now();
  }
};

export const usePageTracking = () => {
  const { user } = useAuth();
  const location = useLocation();
  const entryIdRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const pageKey = pathToPageKey(location.pathname);
    const sessionId = getSessionId();
    let cancelled = false;

    // Page-enter
    axios
      .post(
        `${API_URL}/api/analytics/page-enter`,
        { page_key: pageKey, session_id: sessionId },
        { withCredentials: true }
      )
      .then((res) => {
        if (cancelled) return;
        entryIdRef.current = res.data?.entry_id || null;
      })
      .catch(() => {
        // Silently ignore — analytics must never break the app
      });

    // Beacon on tab close / navigation
    const handleUnload = () => {
      const entryId = entryIdRef.current;
      if (!entryId || !navigator.sendBeacon) return;
      try {
        const blob = new Blob([JSON.stringify({ entry_id: entryId })], {
          type: "application/json",
        });
        navigator.sendBeacon(`${API_URL}/api/analytics/page-leave`, blob);
      } catch {}
    };
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      cancelled = true;
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);

      // On SPA route change, record leave via normal POST
      const entryId = entryIdRef.current;
      if (entryId) {
        axios
          .post(
            `${API_URL}/api/analytics/page-leave`,
            { entry_id: entryId },
            { withCredentials: true }
          )
          .catch(() => {});
        entryIdRef.current = null;
      }
    };
  }, [location.pathname, user]);
};

export default usePageTracking;
